import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPinPlus, Plus, X, Copy, Check } from 'lucide-react';
import { CHARGER_IDENTITY_RE } from '@evi/shared';
import {
  submitNetworkSubmission,
  fetchMyNetworkSubmissions,
  checkSubmissionIdentities,
  fetchFleets,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './NetworkSubmissions.module.css';
import { DeploymentPage } from './DeploymentPage';
import { buildSubmitLocationBody } from './submitLocationBody';
import { PlaceAutocomplete } from '@/components/ui/PlaceAutocomplete';
import {
  CONNECTIVITY_CHOICE_ERROR, CONNECTIVITY_FIELD_LABEL, CONNECTIVITY_OPTIONS, CONNECTIVITY_SET_HINT, connectivityTicked,
  connectivityToggled, type ConnectivityDraft,
} from './connectivityChoice';

/**
 * Submit a Location — the authenticated submitter surface for SPs and
 * fleet managers. Proposes a location + its chargers (one row per
 * physical unit, identity exactly as configured on the hardware); the
 * commissioning flow creates credentials automatically for qualified,
 * reviewed partners, or after operator authorization. Launch is separate.
 * Shows the stage-correct OCPP websocket endpoint with the
 * copyable treatment, and the caller's past submissions.
 */

type UnitRow = {
  identity: string;
  vendor: string;
  model: string;
  connectors: string[];
  maxKw: string;
};

const EMPTY_UNIT: UnitRow = { identity: '', vendor: '', model: '', connectors: ['J1772'], maxKw: '' };

const LOCATION_TYPES = [
  { value: 'fleet', label: 'Fleet depot' },
  { value: 'multifamily', label: 'Multifamily building' },
  { value: 'public', label: 'Public site' },
  { value: 'commercial', label: 'Workplace / Commercial' },
] as const;

export function SubmitLocationPage() {
  const id = new URLSearchParams(window.location.search).get('deployment');
  return id ? <DeploymentPage deploymentId={id === 'all' ? undefined : id} /> : <SubmissionForm />;
}
function SubmissionForm() {
  useRecentRoute('Submit a Location');
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isFleetAdmin = user?.role === 'fleet_admin';
  const needsSalesClaim = !!user?.is_sales_agent || ['sales_rep', 'sales_manager'].includes(user?.template ?? '');

  const { data: mine } = useQuery({
    queryKey: ['network-submissions', 'mine'],
    queryFn: fetchMyNetworkSubmissions,
    staleTime: 30_000,
  });
  const { data: fleets = [] } = useQuery({
    queryKey: ['fleets', 'admin'],
    queryFn: fetchFleets,
    staleTime: 60_000,
    enabled: isFleetAdmin,
  });

  const [expectedInstall, setExpectedInstall] = useState(''), [powerClass, setPowerClass] = useState<'AC' | 'DC'>('AC');
  const [name, setName] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState(''), [lat, setLat] = useState(''), [lng, setLng] = useState('');
  const clearSalesPin = () => { if (needsSalesClaim) { setLat(''); setLng(''); } };
  const [locType, setLocType] = useState<string>(isFleetAdmin ? 'fleet' : 'commercial');
  const [fleetId, setFleetId] = useState('');
  const [contactName, setContactName] = useState(user?.name ?? '');
  const [contactEmail, setContactEmail] = useState(user?.email ?? '');
  const [contactPhone, setContactPhone] = useState('');
  const [units, setUnits] = useState<UnitRow[]>([{ ...EMPTY_UNIT }]);
  // Required per deployment (Warren, 2026-10-01); one choice for every unit.
  const [connectivity, setConnectivity] = useState<ConnectivityDraft>('');
  const [dupWarnings, setDupWarnings] = useState<Map<string, string>>(new Map());
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [deploymentId, setDeploymentId] = useState('');
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());

  // Inline duplicate warnings (authenticated surface only) — debounced
  // probe of valid identities against live chargers + pending submissions.
  useEffect(() => {
    const valid = units.map((u) => u.identity.trim()).filter((i) => CHARGER_IDENTITY_RE.test(i));
    if (valid.length === 0) { setDupWarnings(new Map()); return; }
    const t = window.setTimeout(() => {
      checkSubmissionIdentities(valid)
        .then((flags) => {
          setDupWarnings(new Map(flags.map((f) => [
            f.identity,
            f.kind === 'existing_charger'
              ? 'Already registered on the network'
              : 'Already in a pending submission',
          ])));
        })
        .catch(() => setDupWarnings(new Map()));
    }, 500);
    return () => window.clearTimeout(t);
  }, [units]);

  const setUnit = (i: number, patch: Partial<UnitRow>) =>
    setUnits((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = useMutation({
    mutationFn: () => {
      // Save stays disabled until it is chosen; this only guards a stale state.
      if (!connectivity) return Promise.reject(new Error(CONNECTIVITY_CHOICE_ERROR));
      if (needsSalesClaim && (!lat.trim() || !lng.trim())) return Promise.reject(new Error('Choose the site map pin.'));
      const body = buildSubmitLocationBody({
        contactName, contactEmail, contactPhone, isFleetAdmin,
        name, street, city, state, zip, locType, fleetId, units, connectivity,
        ...(needsSalesClaim ? { siteClaim: { country, lat: Number(lat), lng: Number(lng) } } : {}),
      });
      return submitNetworkSubmission({ ...body, ...(needsSalesClaim ? { job_plan: { power_class: powerClass, expected_install_date: expectedInstall } } : {}) }, requestKey);
    },
    onSuccess: (result) => {
      setDeploymentId(result.submission.id);
      setSubmitted(true);
      qc.invalidateQueries({ queryKey: ['network-submissions', 'mine'] });
    },
    onError: async (err) => {
      const httpErr = err as { response?: Response; message?: string };
      let msg = httpErr.message ?? 'Submission failed';
      if (httpErr.response) {
        try {
          const j = await httpErr.response.clone().json() as { error?: { message?: string } };
          msg = j.error?.message ?? msg;
        } catch { /* keep generic */ }
      }
      setSubmitError(msg);
    },
  });

  const identitiesValid = units.every((u) => CHARGER_IDENTITY_RE.test(u.identity.trim()));
  const formValid =
    name.trim() && street.trim() && city.trim() && state.trim() && zip.trim()
    && contactName.trim() && contactEmail.trim()
    && units.length > 0 && identitiesValid
    && !!connectivity
    && (!needsSalesClaim || !!expectedInstall)
    && (!needsSalesClaim || country.trim() && lat.trim() && lng.trim() && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)) && Math.abs(Number(lat)) <= 90 && Math.abs(Number(lng)) <= 180)
    && (!isFleetAdmin || locType !== 'fleet' || fleetId);

  const ocppUrl = mine?.ocppUrl ?? 'wss://ocpp.stg.evinitiative.com';

  if (submitted) {
    return (
      <div className={styles.page}>
        <div className={styles.successBanner}>
          Deployment saved. Commissioning authorizes programming; a human approves launch after the checks pass.
        </div>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={() => {
            setSubmitted(false); setRequestKey(crypto.randomUUID());
            setName(''); setStreet(''); setCity(''); setState(''); setZip('');
            setCountry(''); setLat(''); setLng('');
            setUnits([{ ...EMPTY_UNIT }]); setConnectivity('');
          }}
        >
          Submit another location
        </button>
        <DeploymentPage deploymentId={deploymentId || undefined} />
        <MySubmissions mine={mine?.submissions ?? []} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><MapPinPlus size={18} /></div>
          <div>
            <h1 className={styles.title}>Submit a Location</h1>
            <p className={styles.subtitle}>
              Register a site before programming its chargers. Commissioning provides credentials; launch follows testing and human approval.
            </p>
          </div>
        </div>
      </div>

      <p className={styles.fieldHint}>
        {user?.is_sales_agent && <>For a customer install, you can use <a href="/register-install">Register Install</a> instead. Choose one form per deployment. </>}
        <a href="/charger-install-guide.html" target="_blank" rel="noreferrer">Read the buyer and installer guide</a>.
      </p>
      <div className={styles.endpointCard}>
        <p className={styles.detailLabel}>OCPP server address (configure this on each unit)</p>
        <div className={styles.endpointRow}>
          <code className={styles.endpointCode}>{ocppUrl}/&lt;charger identity&gt;</code>
          <CopyButton value={ocppUrl} />
        </div>
        <p className={styles.fieldHint}>
          The websocket URL must end in the unit&rsquo;s exact identity, the same identity
          you enter below. After authorization, open Deployment commissioning to reveal this unit&rsquo;s
          password and enter it in the manufacturer&rsquo;s setup tool. The URL alone is not enough.
        </p>
      </div>

      <form
        className={styles.form}
        onSubmit={(e) => { e.preventDefault(); setSubmitError(''); save.mutate(); }}
      >
        {needsSalesClaim && <section><h2>Site address and map pin</h2><p>Choose the physical site. Existing sites require Super Admin review before a new sales claim can earn.</p>
          <PlaceAutocomplete onSelect={a => { setStreet(a.street); setCity(a.city); setState(a.state); setZip(a.zip); setCountry(a.country); setLat(String(a.lat)); setLng(String(a.lng)); }} />
          <div className={styles.fieldGrid2}><label className={styles.field}><span className={styles.label}>Country</span><select className={styles.input} value={country} onChange={e => { setCountry(e.target.value); clearSalesPin(); }} required><option value="">Choose country</option><option value="US">United States</option><option value="CA">Canada</option><option value="BM">Bermuda</option></select></label>
            <label className={styles.field}><span className={styles.label}>Site latitude</span><input className={styles.input} type="number" step="any" min="-90" max="90" required value={lat} onChange={e => setLat(e.target.value)} /></label><label className={styles.field}><span className={styles.label}>Site longitude</span><input className={styles.input} type="number" step="any" min="-180" max="180" required value={lng} onChange={e => setLng(e.target.value)} /></label></div>
        </section>}
        <div className={styles.fieldGrid2}>
          <label className={styles.field}>
            <span className={styles.label}>Location name</span>
            <input className={styles.input} value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Type</span>
            <select className={styles.input} value={locType} onChange={(e) => setLocType(e.target.value)}>
              {LOCATION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className={styles.field} role="group" aria-label={CONNECTIVITY_FIELD_LABEL}>
          <span className={styles.label}>{CONNECTIVITY_FIELD_LABEL}</span>
          <div className={styles.radioRow}>
            {CONNECTIVITY_OPTIONS.map((o) => (
              <label key={o.value} className={styles.radioOption}>
                <input
                  type="checkbox"
                  name={`connectivity-${o.value}`}
                  checked={connectivityTicked(connectivity, o.value)}
                  onChange={(e) => setConnectivity((d) => connectivityToggled(d, o.value, e.target.checked))}
                />
                <span>
                  <strong>{o.label}</strong>
                  <span className={styles.fieldHint} style={{ display: 'block' }}>{o.hint}</span>
                </span>
              </label>
            ))}
          </div>
          <p className={styles.fieldHint}>
            {connectivity ? CONNECTIVITY_SET_HINT : CONNECTIVITY_CHOICE_ERROR}
          </p>
        </div>

        {isFleetAdmin && locType === 'fleet' && (
          <label className={styles.field}>
            <span className={styles.label}>Fleet</span>
            <select className={styles.input} value={fleetId} onChange={(e) => setFleetId(e.target.value)} required>
              <option value="" disabled>Choose a fleet…</option>
              {fleets.map((f) => <option key={f._id} value={f._id}>{f.name}</option>)}
            </select>
          </label>
        )}

        <label className={styles.field}>
          <span className={styles.label}>Street address</span>
          <input className={styles.input} value={street} onChange={(e) => { setStreet(e.target.value); clearSalesPin(); }} required maxLength={200} />
        </label>
        <div className={styles.fieldGrid2}>
          <label className={styles.field}>
            <span className={styles.label}>City</span>
            <input className={styles.input} value={city} onChange={(e) => { setCity(e.target.value); clearSalesPin(); }} required maxLength={100} />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>State / Province</span>
            <input className={styles.input} value={state} onChange={(e) => { setState(e.target.value); clearSalesPin(); }} required maxLength={100} />
          </label>
        </div>
        <div className={styles.fieldGrid2}>
          <label className={styles.field}>
            <span className={styles.label}>ZIP / Postal code</span>
            <input className={styles.input} value={zip} onChange={(e) => { setZip(e.target.value); clearSalesPin(); }} required maxLength={20} />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Contact phone <span className={styles.muted}>(optional)</span></span>
            <input className={styles.input} value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} maxLength={32} />
          </label>
        </div>
        <div className={styles.fieldGrid2}>
          <label className={styles.field}>
            <span className={styles.label}>Contact name</span>
            <input className={styles.input} value={contactName} onChange={(e) => setContactName(e.target.value)} required maxLength={120} />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Contact email</span>
            <input className={styles.input} type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} required maxLength={254} />
          </label>
        </div>

        {needsSalesClaim && (
          <div role="group" aria-labelledby="job-plan-heading">
            <p id="job-plan-heading" className={styles.detailLabel}>Job plan</p>
            <div className={styles.fieldGrid2}>
              <label className={styles.field}>
                <span className={styles.label}>Power class</span>
                <select className={styles.input} value={powerClass} onChange={(e) => setPowerClass(e.target.value as 'AC' | 'DC')}>
                  <option value="AC">AC (Level 2)</option>
                  <option value="DC">DC fast</option>
                </select>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Expected install date</span>
                <input className={styles.input} type="date" value={expectedInstall} onChange={(e) => setExpectedInstall(e.target.value)} required />
              </label>
            </div>
            <p className={styles.fieldHint}>Charger count: {units.length}, the units below. The contact above is the site contact.</p>
          </div>
        )}

        <p className={styles.detailLabel}>Chargers: one entry per physical unit</p>
        {units.map((u, i) => {
          const trimmed = u.identity.trim();
          const badFormat = trimmed.length > 0 && !CHARGER_IDENTITY_RE.test(trimmed);
          const dup = dupWarnings.get(trimmed);
          return (
            <div key={i} className={styles.unitCard}>
              <div className={styles.unitHead}>
                <p className={styles.unitTitle}>Unit {i + 1}</p>
                {units.length > 1 && (
                  <button
                    type="button"
                    className={styles.removeBtn}
                    aria-label={`Remove unit ${i + 1}`}
                    onClick={() => setUnits((rows) => rows.filter((_, idx) => idx !== i))}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <label className={styles.field}>
                <span className={styles.label}>Charger identity</span>
                <input
                  className={`${styles.input} ${badFormat || dup ? styles.inputError : ''}`}
                  value={u.identity}
                  onChange={(e) => setUnit(i, { identity: e.target.value })}
                  required
                  placeholder="e.g. CU89TVWKPVJ8"
                />
                {badFormat && (
                  <p className={styles.fieldWarn}>
                    Letters, numbers, dashes, and underscores only (max 50).
                  </p>
                )}
                {!badFormat && dup && <p className={styles.fieldWarn}>{dup}</p>}
                {!badFormat && !dup && (
                  <p className={styles.fieldHint}>
                    Exactly as configured on the unit: the last segment of its websocket URL.
                  </p>
                )}
              </label>
              <div className={styles.fieldGrid2}>
                <label className={styles.field}>
                  <span className={styles.label}>Vendor <span className={styles.muted}>(optional)</span></span>
                  <input className={styles.input} value={u.vendor} onChange={(e) => setUnit(i, { vendor: e.target.value })} maxLength={100} />
                </label>
                <label className={styles.field}>
                  <span className={styles.label}>Model <span className={styles.muted}>(optional)</span></span>
                  <input className={styles.input} value={u.model} onChange={(e) => setUnit(i, { model: e.target.value })} maxLength={100} />
                </label>
              </div>
              <div className={styles.fieldGrid2}>
                <div>
                  {u.connectors.map((type, plug) => <label key={plug} className={styles.field}>
                    <span className={styles.label}>Unit {i + 1}, plug {plug + 1} type</span>
                    <select className={styles.input} value={type}
                      onChange={e => setUnit(i, { connectors: u.connectors.map((t, n) => n === plug ? e.target.value : t) })}>
                      <option value="J1772">J1772</option><option value="NACS">NACS</option><option value="CCS1">CCS1</option>
                    </select>
                  </label>)}
                  <p className={styles.fieldHint}>List every physical plug in its OCPP connector order. Each plug must pass its own test.</p>
                  <button type="button" className={styles.secondaryBtn} disabled={u.connectors.length >= 8}
                    onClick={() => setUnit(i, { connectors: [...u.connectors, u.connectors[0] ?? 'J1772'] })}>Add plug to unit {i + 1}</button>
                  {u.connectors.length > 1 && <button type="button" className={styles.secondaryBtn}
                    onClick={() => setUnit(i, { connectors: u.connectors.slice(0, -1) })}>Remove last plug</button>}
                </div>
                <label className={styles.field}>
                  <span className={styles.label}>Max power (kW) <span className={styles.muted}>(optional)</span></span>
                  <input
                    className={styles.input}
                    type="number"
                    min="0"
                    max="1000"
                    step="0.1"
                    value={u.maxKw}
                    onChange={(e) => setUnit(i, { maxKw: e.target.value })}
                  />
                </label>
              </div>
            </div>
          );
        })}
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={() => setUnits((rows) => [...rows, { ...EMPTY_UNIT }])}
          disabled={units.length >= 50}
        >
          <Plus size={14} /> Add another unit
        </button>

        {submitError && <div className={styles.errorBanner}>{submitError}</div>}

        <div className={styles.actions}>
          <button type="submit" className={styles.primaryBtn} disabled={!formValid || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save deployment'}
          </button>
        </div>
      </form>

      <a href="/deployments">Open my deployments and assigned installations</a>
      <MySubmissions mine={mine?.submissions ?? []} />
    </div>
  );
}

function MySubmissions({ mine }: { mine: Array<{ id: string; status: string; locationName: string; chargerCount: number; reviewerNote?: string | null; createdAt: string }> }) {
  if (mine.length === 0) return null;
  return (
    <div className={styles.detailSection}>
      <p className={styles.detailLabel}>Your submissions</p>
      <table className={styles.chargerTable}>
        <thead>
          <tr><th>Location</th><th>Chargers</th><th>Status</th><th>Note</th></tr>
        </thead>
        <tbody>
          {mine.map((m) => (
            <tr key={m.id}>
              <td><a href={`/deployments/${m.id}`}>{m.locationName}</a></td>
              <td>{m.chargerCount}</td>
              <td>
                <span className={`${styles.status} ${styles[`status_${m.status}`] ?? ''}`}>
                  {m.status.replace('_', ' ')}
                </span>
              </td>
              <td>{m.reviewerNote ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={styles.copyBtn}
      onClick={() => {
        navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
        }).catch(() => { /* clipboard blocked — the code is selectable */ });
      }}
    >
      {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
    </button>
  );
}
